/**
 * AuthSessionSync — keeps the user store honest about the Supabase session.
 *
 * Mounted once at the app root. On mount it asks Supabase for the current
 * session and reconciles the user store against it (see
 * lib/authSessionReconcile.ts for the decision table), then subscribes to
 * `onAuthStateChange` so a sign-out, expiry, or sign-in in another tab is
 * reflected here too. `RequireAuth` waits for the first check before deciding
 * to redirect, so a valid session with cleared localStorage no longer bounces
 * to the login page.
 *
 * When the session names a different person than the one this page loaded
 * data for, the page reloads once instead of adopting them in place
 * (lib/accountChange.ts): the stores still hold the previous person's rows.
 */

import { useEffect } from 'react';
import { getSupabaseClient, isSupabaseConfigured } from '../../lib/supabaseClient';
import { useUserStore } from '../../stores/prioritization/userStore';
import { reconcileSession, UNKNOWN_USER_ID, type SessionLike } from '../../lib/authSessionReconcile';
import { getPageUserId, notePageUser, reloadOnceForAccountChange } from '../../lib/accountChange';
import { forgetPreviousAccount } from '../../lib/forgetPreviousAccount';
import { setSessionCheckPending as setPending } from '../../lib/authSessionState';
import { forgetMyAiKeys, loadMyAiKeys } from '../../lib/aiProviderKeys';

function isDemoMode(): boolean {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem('peakPriorities_demoMode') === 'true';
  } catch {
    return false;
  }
}

function applySession(session: SessionLike | null) {
  const store = useUserStore.getState();
  const demoMode = isDemoMode();
  // A store restored from storage names the person whose data this page may
  // already be loading (RequireAuth lets a restored user straight through).
  if (!demoMode && !store.isImpersonating && store.userId && store.userId !== UNKNOWN_USER_ID) {
    notePageUser(store.userId);
  }
  const action = reconcileSession({
    session,
    state: { userId: store.userId, isImpersonating: store.isImpersonating },
    demoMode,
    pageUserId: getPageUserId(),
  });
  if (action.kind === 'switch_account') {
    // A different person: every workspace-scoped store still holds the previous
    // person's rows, so start the page over rather than adopt them in place.
    if (reloadOnceForAccountChange(action.userId, { beforeReload: forgetPreviousAccount })) return;
    // Already reloaded for this person a moment ago — adopt in place rather than loop.
    console.warn('[AuthSessionSync] Account changed again right after a reload; adopting in place');
    notePageUser(action.userId, { replace: true });
    store.setUser(action.userId, action.userName, action.userEmail);
  } else if (action.kind === 'sign_out') {
    // The session is already gone, so only the store is left to clear. Ending
    // "the session" here would end whatever is stored by now, which can be a
    // sign-in made a moment ago.
    store.forgetSignedInUser();
  } else if (action.kind === 'adopt_session') {
    store.setUser(action.userId, action.userName, action.userEmail);
  }
  // A sign-out keeps the page user: the next person to sign in on this page
  // gets a fresh page, not the previous person's stores.
  if (session && !demoMode && !store.isImpersonating) notePageUser(session.user.id);
}

export function AuthSessionSync() {
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    let cancelled = false;
    const supabase = getSupabaseClient();

    setPending(true);
    void supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        applySession(data.session);
        // A person's own AI keys live on the server (migration 982); read which are saved.
        if (data.session) void loadMyAiKeys();
      })
      .catch(() => {
        // Could not reach auth at all — leave the store as it is; the next
        // query will surface the real failure.
      })
      .finally(() => {
        if (!cancelled) setPending(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'SIGNED_OUT') {
        // Another tab's sign-out can land after a new sign-in, so read what is
        // stored rather than assume nothing is. Deferred: this callback runs
        // inside the auth lock, and auth calls made here would wait on it.
        setTimeout(() => {
          if (cancelled) return;
          void supabase.auth
            .getSession()
            .catch(() => ({ data: { session: null } }))
            .then(({ data }) => {
              if (cancelled) return;
              applySession(data.session);
              if (!data.session) forgetMyAiKeys();
            });
        }, 0);
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        applySession(session);
        if (event === 'SIGNED_IN') void loadMyAiKeys();
      }
    });

    return () => {
      cancelled = true;
      setPending(false);
      sub.subscription.unsubscribe();
    };
  }, []);

  return null;
}
